import {Tokens} from '@/core/api/helpers';
import {IOrganizationUserInfo} from './organization.interface';
import {IUserOrganizationSettingData} from './organization-notification.interface';
import {IOrganizationTablesTicket} from './organization-dashboard.interface';
import {IPaginationResponse} from './shared.interface';
import {PAYMENT_METHOD} from '../enums';

export interface IUserRegisterLoginResponse {
  userInfo: IOrganizationUserInfo;
  tokens: Tokens;
}

export interface IUserRegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface IUserLoginRequest {
  email: string;
  password: string;
}

export interface IUserTable {
  id: number;
  tableInfoId: number;
  name: string;
  number: number;
  typeOfSeat: string;
  price: number;
  isPaid: boolean;
  purchaserId: number;
  purchaser: string;
  email: string;
  contactPhone: string;
  tickets: IOrganizationTablesTicket[];
  isUserPurchaser: boolean;
}

export interface IUserReducer {
  userInfo: IOrganizationUserInfo | null;
  isLoading: boolean;
  userTables: IUserTable[] | [];
}

export interface IVerifyOldPasswordRequest {
  oldPassword: string;
}

export interface ISetNewPasswordRequest {
  changingPassword: string;
  confirmChangePassword: string;
}

export interface IChengePasswordResponse {
  id: number;
  oldPassword: string;
  changingPassword: string;
  confirmChangePassword: string;
}

export interface IUserChangeProfileRequest {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  meals: string[];
}

export interface IUserChangeProfileResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  meals: string[];
  added: string;
  edited: string;
}

export interface IUserNotificationSettingReducer {
  userSetting: IUserOrganizationSettingData | null;
  isLoading: boolean;
}

export interface IUserSettingReqData {
  reqBody: IUserOrganizationSettingData;
  id: number;
}

export interface IUserDonationData {
  donaterId: number;
  amount: number;
  eventId: number;
}

export interface IDonationData {
  donationId: number;
  donaterId: number;
  userName: string;
  donationAmount: number;
}

export interface IUserDonationReducer {
  totalDonationsAmount: number;
  donations: IDonationData[];
  isLoading: boolean;
}

export interface IUserDonationResponse {
  totalDonationsAmount: number;
  donations: IDonationData[];
}

export interface IUserRaffleTikcersTiersData {
  id?: number;
  qty: number;
  price: number;
}

export interface IUserRaffleReduser {
  ticketTiers: IUserRaffleTikcersTiersData[];
  isLoading: boolean;
}

export interface IRaffleTicketsData extends IUserRaffleTikcersTiersData {
  amount: number;
}

export interface IResetPasswordRequest {
  changingPassword: string;
  confirmChangePassword: string;
  recoveryCode: string;
}

export interface IStripeOnboardingResponse {
  url: string;
  expiresAt: string;
}

export type StripeConnectStatus = 'NotStarted' | 'Pending' | 'Active' | 'Restricted';

export interface StripeAccountDetails {
  stripeEnabled: boolean;
  stripeConnectedAccountId: string;
  stripeConnectAccountStatus: StripeConnectStatus;
  stripeChargesEnabled: boolean;
  stripePayoutsEnabled: boolean;
  stripeDetailsSubmitted: boolean;
  stripeCountry: string;
  stripeDefaultCurrency: string;
  stripeOnboardingCompletedAt: string;
  transactionFee: number;
}
export type PaymentStatus = 'Pending' | 'Completed' | 'Failed' | 'Refunded';
export type AdminPaymentStatus = 'Pending' | 'Paid' | 'Failed' | 'Refunded';

export enum PaymentResult {
  PENDING = 'Pending',
  COMPLETED = 'Completed',
  FAILED = 'Failed',
  REFUNDED = 'Refunded',
}
export interface PaymentRes {
  status: PaymentStatus;
}

export interface IMakeDonationRequest {
  attendeeId?: number;
  eventId: number;
  amount: number;
}

export interface IOrganizationMakeDonationRequest {
  attendeeId: number;
  amount: number;
}

export interface IMakeDonationResponse {
  paymentId: number;
  checkoutSessionId: string;
  url: string;
  expiresAt: string;
}

export interface PaymentOrgRequest {
  eventId: number;
  isCoverFee: boolean;
}

export interface PaymentOrgResponse {
  id: string;
  url: string;
  expiresAt: string;
}

export interface MakePaymentOrgRequest {
  eventId: number;
  notes?: string;
}

export interface IRequestPaymentWithPagination {
  page: number;
  pageSize: number;
  searchName: string;
  sortColumn: string;
  isOrderDesc: true;
  eventId: number;
  status: PaymentStatus;
  fromDate: string;
  toDate: string;
}

export interface IRequestBillingWithPagination {
  page: number;
  pageSize: number;
  searchName?: string;
  sortColumn?: string;
  isOrderDesc: boolean;
  eventId: number;
  status?: PaymentStatus;
  fromDate?: string;
  toDate?: string;
}

export interface IOrganizationBillingListData {
  paymentId: number;
  invoiceFileName: string;
  invoiceDate: string;
  billedToName: string;
  billedToEmail: string;
  amount: number;
  status: PaymentStatus;
  invoiceDownloadUrl: string;
  invoiceUrlExpiresAt: string;
}

export interface IOrganizationBillingListResponse extends IPaginationResponse {
  data: IOrganizationBillingListData[];
}

export interface IBilingListReducer {
  billingHistoryData: IOrganizationBillingListResponse | null;
  isLoading: boolean;
}

export interface IAttendeeIdentity {
  attendeeId: number;
  paymentMethod: PAYMENT_METHOD;
  canLiveBid: boolean;
  hasSavedPaymentMethod: boolean;
}

export interface IAttendeeCardStatus {
  status: 'string';
  setupSessionId: string;
  setupUrl: string;
  expiresAt: string;
}
